import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { serverNow } from '#features/date/utils/serverClock';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import {
  isHeldByVoice,
  nextSessionRoomBoundary,
  pickCurrentSession,
  retainFarewellSession,
} from '../model/currentSession';
import {
  sessionRoomSessionsQueryKey,
  sessionRoomSessionsQueryOptions,
  type SessionRoomEntry,
} from '../model/roomSessions';

/** Одна ссылка на пустой список: новая на каждый рендер пересчитывала бы всё, что от него зависит. */
const NO_SESSIONS: SessionRoomEntry[] = [];

/**
 * Потолок одного ожидания. Сессия на послезавтра дала бы `setTimeout` длиной в двое суток – а он
 * и переполняется на 24.8 дня, и просыпается непредсказуемо после сна машины. Дешевле раз в
 * полчаса проснуться и свериться с часами заново.
 */
const MAX_TIMER_MS = 30 * 60 * 1000;

/** Пересчитываем на секунду позже границы – иначе просыпаемся ровно на ней и видим прежнюю фазу. */
const BOUNDARY_PADDING_MS = 1000;

/**
 * Сессия, которую сейчас показывает комната, вместе с её содержимым.
 *
 * Пересчитывается не только на новых данных, но и по часам: комната обязана появиться за десять
 * минут до старта и исчезнуть в момент окончания сама, без перезагрузки страницы, – ни того, ни
 * другого события сервер не присылает.
 */
export const useCurrentSession = () => {
  const isAuth = useIsAuth();
  const queryClient = useQueryClient();

  /**
   * Комната, в чьём голосе я сижу. Читаем `room` только пока разговор жив: после `reset` он
   * обнуляется, но между выходом и обнулением приоритет успел бы удержать чужую сессию.
   */
  const voiceSessionId = useVoiceStore((state) =>
    state.phase === 'idle' ? null : (state.room?.sessionId ?? null)
  );

  const { data, isLoading } = useQuery({
    ...sessionRoomSessionsQueryOptions(),
    enabled: isAuth,
  });

  const serverSessions = data ?? NO_SESSIONS;

  const [now, setNow] = useState(() => serverNow());

  const [lastShown, setLastShown] = useState<SessionRoomEntry | null>(null);

  /*
    Сессия кончилась, а я ещё в её голосе, и рядом уже идёт или вот-вот начнётся другая. С
    23.09.2026 сервер держит голос доигранной сессии, пока в нём говорят, а её строки в отборе
    больше нет - и комната переключилась бы на следующую: меня слышно в первой, а её микрофона и
    выхода на экране нет. Пока голос держит сессию, которой нет в ответе, отбора не делаем:
    хост остаётся на её прощании. Следующая сессия покажется, как только я выйду из голоса.
  */
  const heldByVoice = isHeldByVoice(voiceSessionId, lastShown?.sessionId ?? null, serverSessions);

  const session = useMemo(
    () => (heldByVoice ? null : pickCurrentSession(serverSessions, { now, voiceSessionId })),
    [heldByVoice, serverSessions, now, voiceSessionId]
  );

  /**
   * Сессия, которую комната показывала последней.
   *
   * Держим снимок целиком, а не один id: за прощание отвечает хост, и ему нужны и игра, и чат, и
   * состав – а взять их будет неоткуда, сервер эту строку уже не отдаёт.
   */
  useEffect(() => {
    if (session) {
      setLastShown(session);
    }
  }, [session]);

  /*
    Отбор ведём по ответу сервера, а память подмешиваем только в список.

    Порядок именно такой, и это несущее различие: попади запомненный снимок в `pickCurrentSession`,
    комната показывала бы сессию, которой у сервера уже нет, – с живым микрофоном и обратным
    отсчётом. Запомненная строка нужна ровно одному потребителю – прощанию: хост ищет пропавшую
    сессию в этом же списке и по её статусу решает, что сказать человеку.
  */
  const farewellSession = useMemo(
    () => (session ? null : retainFarewellSession(lastShown, serverSessions, now)),
    [session, lastShown, serverSessions, now]
  );

  const sessions = useMemo(
    () => (farewellSession ? [...serverSessions, farewellSession] : serverSessions),
    [farewellSession, serverSessions]
  );

  const boundary = useMemo(
    () => nextSessionRoomBoundary(serverSessions, now),
    [serverSessions, now]
  );

  useEffect(() => {
    if (boundary === null) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    /*
     * Ожидание длиннее потолка режем на цепочку отрезков, и следующий отрезок взводим здесь же,
     * внутри самого таймера.
     *
     * Перевзвод через зависимости эффекта тут не работает: `boundary` – абсолютная метка
     * времени, и когда таймер сработал по потолку, `setNow` пересчитывал её в то же самое число.
     * React не видел изменения зависимости, cleanup не звался, нового `setTimeout` не
     * появлялось – и у человека, оставившего вкладку открытой, комната для сессии на вечер не
     * появлялась ни за десять минут до старта, ни в старт: только если повезёт с чужим событием
     * сокета.
     */
    const arm = () => {
      // Задержку считаем от настоящих часов, а не от `now`: состояние отстаёт ровно на время,
      // проведённое вкладкой в фоне, и именно там таймеры и придерживают.
      const untilBoundary = Math.max(boundary - serverNow(), 0) + BOUNDARY_PADDING_MS;

      timer = setTimeout(
        () => {
          if (serverNow() < boundary) {
            // Проснулись по потолку, граница ещё впереди – ждём дальше, рендер тревожить незачем.
            arm();

            return;
          }

          setNow(serverNow());
        },
        Math.min(untilBoundary, MAX_TIMER_MS)
      );
    };

    arm();

    return () => clearTimeout(timer);
  }, [boundary]);

  useEffect(() => {
    /*
     * Возврат на вкладку – отдельный повод пересчитать. Браузер придерживает таймеры фоновой
     * вкладки, и человек, вернувшийся к ноутбуку через час, иначе видел бы комнату давно
     * закончившейся сессии до первого события с сокета.
     *
     * Часами дело не ограничивается: за время в фоне сессию могли отменить, завершить или я мог
     * выйти из неё сам – с телефона, из соседней вкладки, из Discord-бота. Своё собственное
     * действие – единственный случай, когда комнате никто не поможет: событие сокета до актора не
     * доходит, а `refetchOnWindowFocus` у приложения выключен глобально. Поэтому на возврате
     * перечитываем и список; `stale: true` держит это в рамках `staleTime` запроса – человек,
     * щёлкающий вкладками, не платит запросом за каждый щелчок.
     */
    const sync = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      setNow(serverNow());
      void queryClient.refetchQueries({
        queryKey: sessionRoomSessionsQueryKey,
        type: 'active',
        stale: true,
      });
    };

    document.addEventListener('visibilitychange', sync);

    return () => document.removeEventListener('visibilitychange', sync);
  }, [queryClient]);

  return { session, sessions, isLoading };
};
